import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { MDText } from 'i18n-react';

import { MakeLogo } from 'Storybook/core/MakeLogo/MakeLogo';

import texts from '../texts.json';
import { canUseWebP } from '../Utilities';

import type { RenderMakeRowsParam } from './MakesCarousel.types';

import styles from './MakesCarousel.module.scss';

const LocalT = new MDText(texts);

const makeNames: { [key: string]: string } = {
	audi: 'Audi',
	bmw: 'BMW',
	ford: 'Ford',
	'land-rover': 'Land Rover',
	mercedes: 'Mercedes',
	porsche: 'Porsche',
	renault: 'Renault',
	toyota: 'Toyota',
	volkswagen: 'Volkswagen',
};

const desktopMakes = [['toyota', 'renault', 'porsche', 'ford', 'bmw', 'land-rover', 'mercedes', 'volkswagen', 'audi']];

const tabletMakes = [
	['porsche', 'ford', 'bmw', 'land-rover', 'mercedes'],
	['toyota', 'renault', 'volkswagen', 'audi'],
];

const mobileMakes = [
	['ford', 'bmw', 'land-rover'],
	['toyota', 'renault', 'porsche', 'mercedes', 'volkswagen', 'audi'],
];

const MakesCarousel: React.FC = () => {
	const [shouldLoadImages, setShouldLoadImages] = useState(false);
	const [hasWebpSupport, setHasWebpSupport] = useState(false);
	const carouselRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if ('IntersectionObserver' in window) {
			const options = {
				rootMargin: '400px',
				threshold: 0,
			};

			const observer = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting) {
					setShouldLoadImages(true);
					setHasWebpSupport(canUseWebP());
					observer.disconnect();
				}
			}, options);

			if (carouselRef.current) {
				observer.observe(carouselRef.current);
			}

			return () => {
				observer.disconnect();
			};
		}
		setShouldLoadImages(true);

		return undefined;
	}, []);

	const renderMakeRows = (breakpoint: RenderMakeRowsParam = 'desktop') => {
		const makeRowsMap = {
			desktop: desktopMakes,
			mobile: mobileMakes,
			tablet: tabletMakes,
		};

		const makeRows = makeRowsMap[breakpoint] || [];

		return (
			<div className={styles[`${breakpoint}Rows`]}>
				{makeRows.map((row) => (
					<ul key={row.join('-')}>
						{row.map((slug) => (
							<li key={slug}>
								<MakeLogo
									className={cx('mw-i')}
									make={shouldLoadImages ? slug : ''}
									title={makeNames[slug]}
									webp={hasWebpSupport}
								/>
							</li>
						))}
					</ul>
				))}
			</div>
		);
	};

	return (
		<section ref={carouselRef} className={styles.makesCarousel}>
			<h3 className={styles.title}>
				{LocalT.translate('homePage.makesCarousel.newTitle', {
					strong: <strong>{LocalT.translate('homePage.makesCarousel.strong')}</strong>,
				})}
			</h3>

			{renderMakeRows('desktop')}
			{renderMakeRows('tablet')}
			{renderMakeRows('mobile')}
		</section>
	);
};

export default MakesCarousel;
